import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { admin } from './admin-api';
import type { Role } from './admin-api';

const ROLES: Role[] = ['operator', 'executive', 'superadmin'];
const ROLE_TAG: Record<Role, string> = { operator: 'muted', executive: 'blue', superadmin: 'amber' };

export default function Users() {
  const qc = useQueryClient();
  const users = useQuery({ queryKey: ['admin', 'users'], queryFn: admin.users });
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState<Role>('operator');
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => admin.createUser(newEmail, newPassword, newRole),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      setNewEmail('');
      setNewPassword('');
      setErr(null);
    },
    onError: (e) => setErr(e instanceof Error ? e.message : 'Failed to create user'),
  });

  const setRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: Role }) => admin.updateUser(id, { role }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
    onError: (e) => setErr(e instanceof Error ? e.message : 'Failed to change role'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => admin.deleteUser(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
    onError: (e) => setErr(e instanceof Error ? e.message : 'Failed to delete user'),
  });

  return (
    <div>
      <div className="ph">Admin · Users</div>
      <div className="h2">Login management</div>
      <p className="lead">Add or remove user accounts. There must always be at least one admin.</p>

      <div className="panel" style={{ marginBottom: 14 }}>
        <div className="qhead"><span className="qdot" style={{ background: 'var(--amber)' }} />Add user</div>
        {err && <div className="login-err">{err}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto auto', gap: 8, alignItems: 'end' }}>
          <div>
            <label className="small faint" style={{ display: 'block', marginBottom: 6, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Email</label>
            <input className="c2-input" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="user@example.com" />
          </div>
          <div>
            <label className="small faint" style={{ display: 'block', marginBottom: 6, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Password</label>
            <input className="c2-input" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="strong password" />
          </div>
          <div>
            <label className="small faint" style={{ display: 'block', marginBottom: 6, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Role</label>
            <select className="c2-input" value={newRole} onChange={(e) => setNewRole(e.target.value as Role)}>
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <button
            type="button"
            className="btn primary"
            disabled={!newEmail || !newPassword || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'Adding…' : 'Add user'}
          </button>
        </div>
      </div>

      <div className="panel">
        <div className="qhead"><span className="qdot" style={{ background: 'var(--teal)' }} />Existing users</div>
        <table>
          <thead>
            <tr>
              <th>Email</th>
              <th>Role</th>
              <th>Created</th>
              <th>Last login</th>
              <th className="center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.data?.map((u) => (
              <tr key={u.id}>
                <td>{u.email}</td>
                <td>
                  <select
                    className="c2-input"
                    style={{ height: 30, padding: '2px 8px', fontSize: 13 }}
                    value={u.role}
                    onChange={(e) => setRole.mutate({ id: u.id, role: e.target.value as Role })}
                    disabled={setRole.isPending}
                    aria-label={`Role for ${u.email}`}
                  >
                    {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                  {' '}<span className={`tag ${ROLE_TAG[u.role]}`}>{u.role}</span>
                </td>
                <td className="small">{new Date(u.created_at).toLocaleDateString()}</td>
                <td className="small">{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : '—'}</td>
                <td className="center">
                  <button
                    type="button"
                    className="btn ghost"
                    style={{ minHeight: 'auto', padding: '6px 12px', fontSize: 13 }}
                    onClick={() => {
                      if (confirm(`Delete user ${u.email}?`)) remove.mutate(u.id);
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
